import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { ThemeAwareColorTiles } from '../../../../components/forms/ThemeAwareColorTiles';
import {
	THEME_AWARE_COLORS,
	resolveThemeColor,
} from '../../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';

interface ColorButtonProps {
	color: string | null;
	onChange: (color: string | null) => void;
	label?: string;
	allowTransparent?: boolean;
	isModalOpen?: boolean;
	disabled?: boolean;
}

export const ColorButton: React.FC<ColorButtonProps> = ({
	color,
	onChange,
	label,
	allowTransparent = false,
	isModalOpen = false,
	disabled = false,
}) => {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const [showPicker, setShowPicker] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });

	const resolvedColor = resolveThemeColor(color, isDark);
	const colorName = THEME_AWARE_COLORS.find((c) => c.id === color)?.name;

	// Calculate position for fixed positioning (non-modal case only)
	useEffect(() => {
		if (!showPicker || buttonRef.current == null || isModalOpen) {
			return;
		}

		const rect = buttonRef.current.getBoundingClientRect();
		setPosition({
			top: rect.bottom + window.scrollY + 4,
			left: rect.left + window.scrollX,
		});
	}, [showPicker, isModalOpen]);

	useEffect(() => {
		if (!showPicker) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (
				panelRef.current != null &&
				!panelRef.current.contains(event.target as Node) &&
				buttonRef.current != null &&
				!buttonRef.current.contains(event.target as Node)
			) {
				setShowPicker(false);
			}
		};

		// Defer listener registration to avoid the opening click triggering immediate close
		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showPicker]);

	return (
		<div className="relative">
			{label != null && (
				<Text className="block text-xs font-medium mb-1" as="label">
					{t(label)}
				</Text>
			)}
			<button
				ref={buttonRef}
				onClick={(e) => {
					e.stopPropagation();

					if (!disabled) {
						setShowPicker(true);
					}
				}}
				disabled={disabled}
				className={cn(
					'flex items-center gap-2 px-3 py-2 border rounded-md',
					colors.classes.bgPrimary,
					colors.classes.borderDefault,
					!disabled && colors.classes.bgPrimaryHover,
					disabled && 'opacity-50 cursor-not-allowed',
				)}
			>
				<div
					className={cn('w-5 h-5 border rounded', colors.classes.borderDefault)}
					style={{
						backgroundColor:
							color != null ? (resolvedColor ?? undefined) : 'transparent',
					}}
				/>
				<Text className="text-xs">
					{colorName ?? (color != null ? color : t('Default'))}
				</Text>
			</button>

			{showPicker && (
				<div
					ref={panelRef}
					className={cn(
						'z-50 p-3 rounded-lg shadow-lg border',
						colors.classes.bgPrimary,
						colors.classes.borderDefault,
					)}
					style={
						isModalOpen
							? {
									position: 'absolute',
									top: '100%',
									left: 0,
									marginTop: '4px',
									minWidth: '220px',
								}
							: {
									position: 'fixed',
									top: position.top,
									left: position.left,
									minWidth: '220px',
								}
					}
				>
					<ThemeAwareColorTiles
						value={color}
						onChange={(colorId) => {
							onChange(colorId);
							setShowPicker(false);
						}}
					/>

					{color != null && (
						<button
							type="button"
							onClick={() => {
								onChange(null);
								setShowPicker(false);
							}}
							className={cn(
								'flex items-center justify-center gap-1 w-full mt-2 py-1 text-xs rounded',
								colors.classes.textSecondary,
								colors.classes.bgSecondary,
								'hover:opacity-80',
							)}
						>
							<Icon icon={faTimes} size="xs" color={colors.values.textMuted} />
							{t('Clear')}
						</button>
					)}
				</div>
			)}
		</div>
	);
};
